import { withOnClickTracking } from 'auto-tracking';
import { Button, Icon, styled, theme } from 'design-system';
import { useState } from 'react';

interface SubmissionReadyChecklistPropsV1 {
  headerImage: string;
  headerTitle: string;
  level: number;
  data: Record<string, CheckItem[]>;
}

type CheckItem = {
  critical: boolean;
  checkKey: string;
  label: string;
  content: {
    text: string;
    links: {
      text: string;
      url: string;
    }[];
  };
  // Other properties
};

export const SubmissionReadyChecklistV1 = ({
  headerImage,
  headerTitle,
  level = 1,
  data,
}: SubmissionReadyChecklistPropsV1) => {
  const [expandedIndex, setExpandedIndex] = useState('');
  const [subExpandedIndex, setSubExpandedIndex] = useState('');
  const handleExpandToggle = (index: string) => {
    if (index === expandedIndex) {
      setExpandedIndex('');
    } else {
      setExpandedIndex(index);
    }
  };

  const subHandleExpandToggle = (event: React.MouseEvent, index: string) => {
    event.stopPropagation(); // Prevent the click event from reaching the parent
    if (index === subExpandedIndex) {
      setSubExpandedIndex('');
    } else {
      setSubExpandedIndex(index);
    }
  };

  return (
    <>
      <CheckListContainer
        style={{ backgroundColor: theme.colors.white.value }}
        role="region"
        aria-labelledby={`checklist-header-${headerTitle.replace(/\s+/g, '-')}`}
      >
        <CheckListHeader
          id={`checklist-header-${headerTitle.replace(/\s+/g, '-')}`}
        >
          {headerTitle}
          <img src={headerImage} alt={headerTitle} />
        </CheckListHeader>
        {level === 1 && (
          <CheckListItems>
            {data &&
              Object.keys(data).map((grouping) => (
                <div key={grouping}>
                  {data[grouping]?.map((item) => (
                    <div key={item.checkKey}>
                      <Items
                        onClick={() => handleExpandToggle(item.checkKey)}
                        style={{
                          backgroundColor:
                            expandedIndex === item.checkKey
                              ? 'rgba(245, 248, 254, 1)'
                              : theme.colors.white.value,
                          borderRadius: '10px',
                        }}
                        css={{ width: '100%' }}
                        aria-controls={`item-v1-${item.checkKey.replace(
                          /\s+/g,
                          '-',
                        )}`}
                        id={`item-v1-${item.checkKey.replace(/\s+/g, '-')}`}
                      >
                        <Text4
                          expanded={expandedIndex !== item.checkKey}
                          style={{
                            fontWeight:
                              expandedIndex === item.checkKey ? 700 : 500,
                          }}
                        >
                          <ChevronDownButton
                            onClick={() => handleExpandToggle(item.checkKey)}
                            aria-label={`Toggle ${item.label}`}
                          >
                            {expandedIndex === item.checkKey ? (
                              <Icon name={'x'} variant="solid" width={8} />
                            ) : (
                              <Icon name={'Plus'} variant="solid" width={8} />
                            )}
                          </ChevronDownButton>
                          <FlexContainer>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.label,
                              }}
                            />{' '}
                            {expandedIndex !== item.checkKey &&
                              item.critical === true && (
                                <div
                                  style={{
                                    paddingRight: '4px',
                                    paddingLeft: '4px',
                                    verticalAlign: 'middle',
                                    display: 'inline-block',
                                    color: theme.colors.gray10.value,
                                  }}
                                >
                                  <Icon
                                    name={'TriangleExclamation'}
                                    variant="solid"
                                    width={14}
                                  />
                                </div>
                              )}
                          </FlexContainer>
                        </Text4>
                        <PassedCheckLanguageContainer
                          id={`content-v1-${item.checkKey.replace(
                            /\s+/g,
                            '-',
                          )}`}
                        >
                          {item.critical === true &&
                            expandedIndex === item.checkKey && (
                              <StyledCriticalStatementContainer
                                style={{ paddingBottom: '20px' }}
                              >
                                <StyledErrorImageContainer>
                                  <div
                                    style={{
                                      paddingRight: '4px',
                                      paddingLeft: '4px',
                                      verticalAlign: 'middle',
                                      display: 'inline-block',
                                      color: theme.colors.gray10.value,
                                    }}
                                  >
                                    <Icon
                                      name={'TriangleExclamation'}
                                      variant="solid"
                                      width={14}
                                    />
                                  </div>
                                  <StyledCriticalStatement>
                                    [Marked as a critical check by journal
                                    editor] -{' '}
                                  </StyledCriticalStatement>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: item.content?.text,
                                    }}
                                  />
                                </StyledErrorImageContainer>
                              </StyledCriticalStatementContainer>
                            )}
                          {item.critical !== true &&
                            expandedIndex === item.checkKey && (
                              <StyledCriticalStatementContainer
                                style={{ paddingBottom: '20px' }}
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: item.content?.text,
                                  }}
                                />
                              </StyledCriticalStatementContainer>
                            )}
                        </PassedCheckLanguageContainer>
                      </Items>
                    </div>
                  ))}
                </div>
              ))}
          </CheckListItems>
        )}
        {level !== 1 && (
          <CheckListItems>
            {data &&
              Object.keys(data).map((grouping) => (
                <div key={grouping}>
                  <Items
                    onClick={() => handleExpandToggle(grouping)}
                    style={{
                      backgroundColor:
                        expandedIndex === grouping
                          ? 'rgba(245, 248, 254, 1)'
                          : '',
                      borderRadius: '10px',
                    }}
                    css={{ width: '100%' }}
                    aria-controls={`item-v1-${grouping.replace(/\s+/g, '-')}`}
                    id={`item-v1-${grouping.replace(/\s+/g, '-')}`}
                  >
                    <Text4
                      expanded={expandedIndex !== grouping}
                      style={{
                        fontWeight: expandedIndex === grouping ? 600 : 500,
                      }}
                    >
                      <ChevronDownButton
                        onClick={() => handleExpandToggle(grouping)}
                        aria-label={`Toggle ${grouping}`}
                      >
                        {expandedIndex === grouping ? (
                          <Icon name={'x'} variant="solid" width={8} />
                        ) : (
                          <Icon name={'Plus'} variant="solid" width={8} />
                        )}
                      </ChevronDownButton>
                      <FlexContainer>{grouping}</FlexContainer>
                    </Text4>
                    {expandedIndex === grouping && (
                      <div
                        key={grouping}
                        id={`content-v1-${grouping.replace(/\s+/g, '-')}`}
                        style={{ marginBottom: '10px' }}
                      >
                        {data[grouping]?.map((item) => (
                          <div
                            key={item.checkKey}
                            style={{
                              padding: '20px',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                              paddingLeft: '20px',
                              backgroundColor:
                                subExpandedIndex === item.checkKey
                                  ? theme.colors.blue4.value
                                  : '',
                            }}
                          >
                            <Items
                              onClick={(event: React.MouseEvent) =>
                                subHandleExpandToggle(event, item.checkKey)
                              }
                              css={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                              }}
                              aria-controls={`sub-item-v1-${item.checkKey.replace(
                                /\s+/g,
                                '-',
                              )}`}
                              id={`sub-item-v1-${item.checkKey.replace(
                                /\s+/g,
                                '-',
                              )}`}
                            >
                              <Text5
                                expanded={subExpandedIndex !== item.checkKey}
                              >
                                <div
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                  }}
                                  onClick={(event) =>
                                    subHandleExpandToggle(event, item.checkKey)
                                  }
                                >
                                  {subExpandedIndex === item.checkKey ? (
                                    <ChevronRightButton
                                      css={{
                                        fontWeight: 500,
                                        marginRight: 10,
                                        marginTop: -10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      aria-label={`Toggle ${item.label}`}
                                    >
                                      <Icon
                                        name={'chevronDown'}
                                        variant="solid"
                                        width={14}
                                      />
                                    </ChevronRightButton>
                                  ) : (
                                    <ChevronRightButton
                                      css={{
                                        fontWeight: 500,
                                        marginRight: 10,
                                        marginTop: -10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      aria-label={`Toggle ${item.label}`}
                                    >
                                      <Icon
                                        name={'ChevronRight'}
                                        variant="regular"
                                        width={14}
                                      />
                                    </ChevronRightButton>
                                  )}
                                </div>
                                <Text4
                                  style={{
                                    fontSize: '16px',
                                    alignItems: 'center',
                                    paddingLeft: '0px',
                                    textTransform: 'none',
                                    fontWeight:
                                      subExpandedIndex === item.checkKey
                                        ? 600
                                        : 500,
                                    padding: '0px',
                                  }}
                                >
                                  <FlexContainer
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginBottom: 10,
                                    }}
                                  >
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: item.label,
                                      }}
                                    />
                                    {subExpandedIndex !== item.checkKey &&
                                      item.critical === true && (
                                        <div
                                          style={{
                                            paddingRight: '4px',
                                            paddingLeft: '4px',
                                            verticalAlign: 'middle',
                                            display: 'inline-block',
                                            color: theme.colors.gray10.value,
                                          }}
                                        >
                                          <Icon
                                            name={'TriangleExclamation'}
                                            variant="solid"
                                            width={14}
                                          />
                                        </div>
                                      )}
                                  </FlexContainer>
                                </Text4>
                              </Text5>
                              <PassedCheckContainer
                                id={`sub-content-v1-${item.checkKey.replace(
                                  /\s+/g,
                                  '-',
                                )}`}
                              >
                                {item.critical === true &&
                                  subExpandedIndex === item.checkKey && (
                                    <StyledCriticalStatementContainer
                                      style={{ gap: 10 }}
                                    >
                                      <StyledErrorImageContainer>
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                          }}
                                        >
                                          <div
                                            style={{
                                              paddingRight: '6px',
                                              paddingLeft: '4px',
                                              verticalAlign: 'middle',
                                              color: theme.colors.gray10.value,
                                              marginBottom: 4,
                                            }}
                                          >
                                            <Icon
                                              name={'TriangleExclamation'}
                                              variant="solid"
                                              width={16}
                                            />
                                          </div>
                                          <StyledCriticalStatement>
                                            [Marked as a critical check by
                                            journal editor] -{' '}
                                          </StyledCriticalStatement>
                                        </div>
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: item.content?.text,
                                          }}
                                        />
                                      </StyledErrorImageContainer>
                                    </StyledCriticalStatementContainer>
                                  )}
                                {item.critical !== true &&
                                  subExpandedIndex === item.checkKey && (
                                    <StyledCriticalStatementContainer
                                      style={{ gap: 10 }}
                                    >
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: item.content?.text,
                                        }}
                                      />
                                    </StyledCriticalStatementContainer>
                                  )}
                              </PassedCheckContainer>
                            </Items>
                          </div>
                        ))}
                      </div>
                    )}
                  </Items>
                </div>
              ))}
          </CheckListItems>
        )}
      </CheckListContainer>
    </>
  );
};

const PassedCheckLanguageContainer = styled('ul', {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  lineHeight: '24px',
  color: 'rgba(5, 30, 87, 1)',
  padding: '0px',
  marginLeft: '20px',
  marginRight: '20px',
  leadingTrim: 'both',
  textEdge: 'cap',
});

const ChevronRightButton = styled(Button, {
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  color: 'rgba(5, 33, 97, 1)',
  width: '16px',
  height: '16px',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  fontWeight: 900,
});

const CheckListContainer = styled('div', {
  width: '50%',
  backgroundColor: theme.colors.white,
  borderRadius: '15px',
  borderColor: theme.colors.gray2,
  height: 'fit-content',
});
const StyledErrorImageContainer = styled('div', {
  display: 'inline-block',
});

const StyledCriticalStatement = styled('span', {
  fontStyle: 'italic',
});
const CheckListHeader = styled('div', {
  backgroundColor: 'rgba(5, 33, 97, 1)',
  paddingLeft: '35px',
  paddingRight: '28px',
  height: '77px',
  borderRadius: '16px 16px 0px 0px',
  color: theme.colors.white,

  fontSize: '22px',
  fontWeight: '600',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
});

const ChevronDownButton = styled(Button, {
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  marginRight: '10px',
  borderRadius: '50%',
  borderColor: 'rgba(5, 33, 97, 1)',
  color: 'rgba(5, 33, 97, 1)',
  width: '16px',
  height: '16px',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  marginLeft: 'inherit',
  fontWeight: 900,
});

const FlexContainer = styled('div', {
  display: 'flex',
  paddingRight: '10px',
});

// const ImageContainer = styled('div', {
//   paddingLeft: '6px',
// });

const Text4 = styled('div', {
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: '26px',
  fontSize: '18px',
  cursor: 'pointer',
  display: 'flex',
  width: '100%',
  color: 'rgba(5, 30, 87, 1)',
  textTransform: 'capitalize',
  padding: '20px',
  paddingTop: '15px',
  paddingBottom: '15px',
  alignItems: 'center',

  variants: {
    expanded: {
      true: {
        '&:hover': {
          color: 'rgba(0, 84, 241, 1)', // Color on hover when expanded is true
          '&>button': {
            color: 'rgba(0, 84, 241, 1)',
            borderColor: 'rgba(0, 84, 241, 1)',
          },
        },
      },
      false: {
        '&:hover': {
          color: theme.colors.blue12, // Color on hover when expanded is false,
        },
      },
    },
  },

  defaultVariants: {
    expanded: false, // Default value if no variant is provided
  },
});

const Text5 = styled('div', {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  variants: {
    expanded: {
      true: {
        '&:hover': {
          '&>div>button': {
            color: 'rgba(0, 84, 241, 1)',
          },
          '&>div>div': {
            color: 'rgba(0, 84, 241, 1)', // Color on hover when expanded is true
          },
        },
      },
      false: {},
    },
  },

  defaultVariants: {
    expanded: false, // Default value if no variant is provided
  },
});

const PassedCheckContainer = styled('ul', {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  lineHeight: '24px',
  color: 'rgba(5, 30, 87, 1)',
  padding: '0px',
  marginLeft: '0px',
  leadingTrim: 'both',
  textEdge: 'cap',
});

const StyledCriticalStatementContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  // padding: '20px',
  paddingTop: '4px',
  paddingBottom: '4px',
  // marginTop: '-10px',
  flexDirection: 'column',
});

const Items = withOnClickTracking(
  styled('div', {
    //padding: '20px',
    //paddingTop: '10px',
    //paddingBottom: '10px',
    //display:'flex'
  }),
);

const CheckListItems = styled('div', {
  paddingLeft: '15px',
  paddingTop: '15px',
  paddingBottom: '15px',
  paddingRight: '15px',
});

SubmissionReadyChecklistV1.displayName = 'SubmissionReadyChecklistV1';
